import { useState } from 'react';
import { api } from '../api/client';
import type { MinecraftAccount } from '@mcpanel/core';
import type { Role, User } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { Dialog, ErrorAlert } from '../components/ui';
import { CopyBox } from '../components/CopyBox';
import { formatDateTime, roleLabel } from '../format';
import { usePoll } from '../hooks/usePoll';

export function Users() {
  const { me } = useApp();
  if (!me) return null;
  if (me.user?.role !== 'owner') return <p>オーナーだけが使えます</p>;
  return (
    <div className="stack">
      <div className="page-header">
        <h1 data-search-id="users">利用者</h1>
      </div>
      <section className="card stack">
        <p>
          招待リンクを作る → 相手に渡す（24 時間・1 回限り）→ 相手が開いて、ログイン
          ID・パスワード・認証アプリを登録すると参加できます。
        </p>
        <p>オーナーは利用者の管理を含めてすべて、管理者はサーバーの操作・承認、閲覧のみは見るだけです。</p>
        <p>認証アプリやリカバリーコードを失くした人には「再設定リンク」を出してください。</p>
        <p>
          「Minecraft アカウント」に Java 版の名前を登録すると、オーナー・管理者の分が全サーバーの OP（レベル
          4）と許可リストに自動で入ります（サーバーごとにプレイヤー画面で切れます。手で入れた人はそのまま残ります。止まっているサーバーは次に起動したときに入ります）。
        </p>
      </section>
      <UsersManagement />
    </div>
  );
}
function UsersManagement() {
  const { me, notify, ensureSteppedUp } = useApp();
  const users = usePoll(() => api.users(), 30000);
  const accounts = usePoll(() => api.minecraftAccounts(), 30000);
  const invites = usePoll(() => api.invites(), 30000);
  const [inviteRole, setInviteRole] = useState<Role>('admin');
  const [inviteUrl, setInviteUrl] = useState<string>();

  const createInvite = async () => {
    try {
      const result = await api.createInvite(inviteRole);
      setInviteUrl(result.url);
      await invites.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const changeRole = async (id: string, role: Role) => {
    if (
      role === 'owner' &&
      !window.confirm(
        'この利用者をオーナーにしますか？利用者の追加・権限の変更・無効化を含め、すべての操作ができるようになります。オーナーをほかのオーナーが外すことは画面からはできません。',
      )
    )
      return;
    try {
      await api.updateUser(id, { role });
      await users.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const toggleDisabled = async (id: string, disabled: boolean) => {
    if (disabled && !window.confirm('この利用者を無効にしますか？ログイン中のセッションとトークンも使えなくなります。'))
      return;
    try {
      await api.updateUser(id, { disabled });
      await users.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const reset = async (userId: string) => {
    try {
      await ensureSteppedUp();
      setInviteUrl((await api.resetUser(userId)).url);
    } catch (caught) {
      notify(errorMessage(caught), true);
    }
  };
  const cancelInvite = async (id: string) => {
    try {
      await api.deleteInvite(id);
      await invites.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };
  const pending = (invites.data ?? []).filter((i) => !i.usedAt && new Date(i.expiresAt) > new Date());

  return (
    <section className="card">
      <div className="card-header">
        <h2>利用者</h2>
        <div className="row">
          <select
            className="input"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as Role)}
            aria-label="招待する役割"
          >
            <option value="admin">管理者</option>
            <option value="viewer">閲覧のみ</option>
          </select>
          <button className="btn small" onClick={() => void createInvite()}>
            招待リンクを作成
          </button>
        </div>
      </div>
      <ErrorAlert error={users.error} />
      <ErrorAlert error={invites.error} />
      <ErrorAlert error={accounts.error} />
      <div className="table-wrap">
        <table>
          <tbody>
            {(users.data ?? []).map((u) => (
              <tr key={u.id}>
                <td>
                  {u.name} {u.disabledAt && <span className="tag">無効</span>}
                </td>
                <td>
                  {u.role === 'owner' || u.id === me?.user?.id ? (
                    <span className="small">{roleLabel[u.role]}</span>
                  ) : (
                    <select
                      className="input"
                      value={u.role}
                      onChange={(e) => void changeRole(u.id, e.target.value as Role)}
                      aria-label={`${u.name} の役割`}
                    >
                      <option value="owner">オーナー</option>
                      <option value="admin">管理者</option>
                      <option value="viewer">閲覧のみ</option>
                    </select>
                  )}
                </td>
                <td>
                  <MinecraftAccounts
                    user={u}
                    accounts={(accounts.data ?? []).filter((a) => a.userId === u.id)}
                    onChange={() => void accounts.reload()}
                  />
                </td>
                <td style={{ textAlign: 'right' }}>
                  {u.id !== me?.user?.id && !u.disabledAt && (
                    <button className="btn small" onClick={() => void reset(u.id)}>
                      再設定リンク
                    </button>
                  )}
                  {u.role !== 'owner' && u.id !== me?.user?.id && (
                    <button className="btn ghost small" onClick={() => void toggleDisabled(u.id, !u.disabledAt)}>
                      {u.disabledAt ? '有効にする' : '無効にする'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pending.length > 0 && (
        <div className="card-body small muted">
          未使用の招待:{' '}
          {pending.map((i) => (
            <div className="row" key={i.id}>
              <span>
                {roleLabel[i.role]}（{formatDateTime(i.expiresAt)} まで）
              </span>
              <button className="btn small" onClick={() => void cancelInvite(i.id)}>
                取り消す
              </button>
            </div>
          ))}
        </div>
      )}
      {inviteUrl && (
        <Dialog
          title="招待・再設定リンク"
          onClose={() => setInviteUrl(undefined)}
          actions={
            <button className="btn primary" onClick={() => setInviteUrl(undefined)}>
              閉じる
            </button>
          }
        >
          <p>24 時間有効で、1 回だけ使えます。招待する人に直接渡してください。</p>
          <CopyBox value={inviteUrl} />
        </Dialog>
      )}
    </section>
  );
}

function MinecraftAccounts({
  user,
  accounts,
  onChange,
}: {
  user: User;
  accounts: MinecraftAccount[];
  onChange: () => void;
}) {
  const { notify } = useApp();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      onChange();
      return true;
    } catch (error) {
      notify(errorMessage(error), true);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    if (await run(() => api.addMinecraftAccount(user.id, name.trim()))) setName('');
  };
  const remove = (account: MinecraftAccount) => {
    if (!window.confirm(`${account.name} の登録を外しますか？パネルが入れた OP と許可リストからも外れます。`)) return;
    void run(() => api.removeMinecraftAccount(user.id, account.uuid));
  };
  return (
    <div className="stack" style={{ gap: 4 }}>
      {accounts.length > 0 && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
          {accounts.map((account) => (
            <span className="tag" key={account.uuid}>
              {account.name}{' '}
              <button
                className="btn ghost small"
                disabled={busy}
                onClick={() => remove(account)}
                aria-label={`${account.name} の登録を外す`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) void add();
        }}
      >
        <input
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Minecraft の名前"
          aria-label={`${user.name} の Minecraft アカウント`}
          maxLength={16}
        />
        <button className="btn small" type="submit" disabled={busy || !name.trim()}>
          追加
        </button>
      </form>
      {user.role === 'viewer' && accounts.length > 0 && (
        <span className="small muted">閲覧のみの人は許可リストに入りません</span>
      )}
    </div>
  );
}
